<script setup lang="ts" generic="T extends string | number">
/**
 * Button toggle group for mutually exclusive selections
 * Wraps v-btn-toggle to centralize usage and provide type-safe API
 * @example
 * <ButtonToggle
 *   v-model="selectedValue"
 *   :options="[
 *     { value: 1, label: '1' },
 *     { value: 2, label: '2' },
 *     { value: 3, label: '3' }
 *   ]"
 *   size="x-small"
 *   density="compact"
 * />
 */
import { computed } from 'vue'
import DecorativeIcon from './DecorativeIcon.vue'

export interface ButtonToggleOption<T extends string | number = string | number> {
  /** Option value for v-model */
  value: T
  /** Display label (optional if icon is provided) */
  label?: string
  /** Optional MDI icon - can be used alone or with label */
  icon?: string
  /** Optional color override for this button (e.g. 'success', 'error') */
  color?: string
}

interface Props<T extends string | number> {
  /** Current selected value */
  modelValue: T
  /** Array of options to display */
  options: ButtonToggleOption<T>[]
  /** Require a selection (default: true) */
  mandatory?: boolean
  /** Toggle density */
  density?: 'default' | 'comfortable' | 'compact'
  /** Button size */
  size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large'
  /** Toggle variant */
  variant?: 'default' | 'outlined' | 'elevated' | 'flat' | 'tonal' | 'text' | 'plain'
  /** Show dividers between buttons */
  divided?: boolean
  /** Default color for buttons (can be overridden per option) */
  color?: string
  /** Make buttons grow to fill container width */
  grow?: boolean
  /** Custom CSS class for the toggle container */
  customClass?: string
  /** Section tag for test automation (data-testid) */
  sectionTag?: string
}

const props = withDefaults(defineProps<Props<T>>(), {
  mandatory: true,
  density: 'default',
  size: 'default',
  variant: 'default',
  divided: false,
  color: 'primary',
  grow: false,
  sectionTag: 'button-toggle'
})

const emit = defineEmits<{
  'update:modelValue': [value: T]
}>()

const internalValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const buttonClass = computed(() => {
  return props.grow ? 'flex-grow-1' : undefined
})
</script>

<template>
  <v-btn-toggle
    v-model="internalValue"
    :mandatory="mandatory"
    :density="density"
    :variant="variant"
    :divided="divided"
    :color="color"
    :class="customClass"
    :section-tag="sectionTag"
  >
    <v-btn
      v-for="option in options"
      :key="String(option.value)"
      :value="option.value"
      :size="size"
      :color="option.color || color"
      :class="buttonClass"
    >
      <DecorativeIcon
        v-if="option.icon"
        :icon="option.icon"
        size="18"
        :className="option.label ? 'mr-2' : undefined"
      />
      <span v-if="option.label">{{ option.label }}</span>
    </v-btn>
  </v-btn-toggle>
</template>
